import { useEffect, useMemo, useRef, useState } from 'react';
import { TadsWidget } from 'react-tads-widget';
import { initRichAds, showRichAdsBanner } from '../../utils/richads';
import { adLog } from '../../utils/adDebug';

// Две сети в одной карточке:
// 1) TAds TGB (type="static", виджет #12950) — рендерится сразу, встраивается по месту;
// 2) RichAds — конвейер захвата виджета в карточку; если фид включат и виджет
//    будет захвачен с контентом, он подменяет TAds (приоритет у RichAds).
// Виджет TAds привязывается к сайту (домену) в кабинете — как appId у RichAds:
//   privately-cultured-mouflon.cloudpub.ru → #12950 (сайт #2088 «myta»)
//   test.mytadating.site                   → #12951 (сайт #2089 «myta-test»)
const TADS_WIDGET_BY_HOST = [
  { match: /(^|\.)privately-cultured-mouflon\.cloudpub\.ru$/i, id: '12950' },
  { match: /(^|\.)mytadating\.site$/i, id: '12951' }
];
const TADS_WIDGET_FALLBACK_ID = '12951';

const resolveTadsWidgetId = () => {
  const host = window.location.hostname;
  const hit = TADS_WIDGET_BY_HOST.find((h) => h.match.test(host));
  return { id: hit ? hit.id : TADS_WIDGET_FALLBACK_ID, host };
};
// Реальный фид виджетов пока пуст (нет кампаний у TAds — проверено запросом
// к backend.tads.me/ads_backend). В dev-сборке карточка при пустом реальном
// фиде автоматически переключается на тестовый режим, чтобы UI оставался
// живым. В прод-сборке тестовых объявлений НЕ показываем — только реальный
// фид, иначе заглушка. С появлением кампаний всё заработает само.
const TADS_TEST_FALLBACK = process.env.NODE_ENV !== 'production';

export function AdBannerCard({ onClose, closeDelaySec = 5 }) {
  const richSlotRef = useRef(null);
  const [richAdsShown, setRichAdsShown] = useState(false);
  const [tadsNotFound, setTadsNotFound] = useState(false);
  const [tadsDebug, setTadsDebug] = useState(false);
  const [secLeft, setSecLeft] = useState(closeDelaySec);
  const tadsWidget = useMemo(() => resolveTadsWidgetId(), []);
  useEffect(() => {
    adLog(`Карточка рекламы открыта (после 10 свайпов); домен ${tadsWidget.host} → TAds виджет #${tadsWidget.id}`);
    let cancelled = false;
    let disposeSession = null;

    initRichAds().then((ok) => {
      if (cancelled) return;
      if (!ok || !richSlotRef.current) {
        adLog('Карточка: RichAds недоступен — работает TAds');
        return;
      }
      showRichAdsBanner(richSlotRef.current).then(({ shown, dispose }) => {
        if (cancelled) {
          dispose();
          return;
        }
        disposeSession = dispose;
        if (shown) {
          adLog('Карточка: RichAds баннер показан — приоритет над TAds');
          setRichAdsShown(true);
        }
      });
    });

    return () => {
      cancelled = true;
      disposeSession?.();
      adLog('Карточка рекламы закрыта');
    };
  }, [tadsWidget]);

  useEffect(() => {
    if (secLeft <= 0) return undefined;
    const timer = setTimeout(() => setSecLeft((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [secLeft]);

  const canClose = secLeft <= 0;
  const showPlaceholder = !richAdsShown && tadsNotFound;

  return (
    <div className="absolute inset-0 z-50 rounded-2xl overflow-hidden bg-[#022424] flex items-center justify-center">
      {/* Слот RichAds: сюда страж переносит виджет, если фид включат */}
      <div
        ref={richSlotRef}
        data-our="richads-slot"
        className={`w-full h-full overflow-hidden flex items-center justify-center ${richAdsShown ? '' : 'hidden'}`}
      />

      {/* TAds TGB — встраиваемый баннер. Сначала реальный фид; если пуст —
          автоматический фоллбэк в тестовый режим (иначе заглушка) */}
      {!richAdsShown && !tadsNotFound && (
        <div data-our="tads-slot" className="w-full h-full overflow-hidden flex items-center justify-center">
          <TadsWidget
            key={`tads-${tadsWidget.id}-${tadsDebug ? 'debug' : 'live'}`}
            id={tadsWidget.id}
            type="static"
            debug={tadsDebug}
            onShowReward={() => adLog(`[TAds] показ засчитан (${tadsDebug ? 'тестовый фид' : 'реальный фид'})`)}
            onClickReward={() => adLog('[TAds] клик по рекламе (onClickReward)')}
            onAdsNotFound={() => {
              if (!tadsDebug && TADS_TEST_FALLBACK) {
                adLog('[TAds] реальный фид пуст — переключаюсь на тестовый режим');
                setTadsDebug(true);
                return;
              }
              adLog('[TAds] ads not found — нет объявлений для пользователя');
              setTadsNotFound(true);
            }}
          />
        </div>
      )}

      {showPlaceholder && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none">
          <p className="text-[22px] font-raleway font-medium text-white/70">Реклама</p>
          <p className="text-[14px] text-white/40">Сейчас нет доступных объявлений</p>
        </div>
      )}

      {/* Крестик закрытия: до истечения таймера показывает обратный отсчёт */}
      <button
        type="button"
        onClick={canClose ? onClose : undefined}
        disabled={!canClose}
        aria-label={canClose ? 'Закрыть рекламу' : `Рекламу можно будет закрыть через ${secLeft} сек`}
        className={`absolute top-3 right-3 z-[9999] flex items-center justify-center w-[38px] h-[38px] rounded-full font-raleway text-[15px] font-medium transition-colors ${
          canClose ? 'bg-white/10 text-white cursor-pointer active:bg-black/70' : 'bg-white/10 text-white/50 cursor-not-allowed'
        }`}
      >
        {canClose ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : (
          <span className="tabular-nums">{secLeft}</span>
        )}
      </button>
    </div>
  );
}
